<template>
  <div :class="{'customized-affiliate': !!affiliateCustomizations }" :style="rootStyle">
    <div class="main-content">
      <div id="vue-loaded" data-cy="vue-loaded" hidden></div>
      <NuxtLoadingIndicator :color="primaryColor" />
      <!-- Single toast host for every layout; see composables/useToast.ts -->
      <UiToastHost />
      <NuxtLayout>
        <NuxtPage />
      </NuxtLayout>
    </div>
  </div>
</template>

<script setup>
import { DEFAULT_THEME_COLOR } from '~/libs/customizations'
import env from '~/libs/env'
import { useAffiliateCustomizationStore } from '~/store/affiliate-customization'
import { useAppStore } from '~/store/app'
const { nuxtBaseUrl } = useApplicationConstants()

// Affiliate customization support
const { appNavLogo: logo, affiliateCustomizations, isLendioTenant, appStyleProps } = storeToRefs(useAffiliateCustomizationStore())

const primaryColor = computed(() => {
  return appStyleProps.value?.['--app-primary-color'] || '#000'
})

const appStore = useAppStore()
const rootStyle = computed(() => {
  const style = {}
  if (appStore.mainBanner.show) {
    style['--offset-top'] = '120px'
  }
  return style
})

// App loading state - starts true to show immediately
const appLoading = ref(true)
const loadingProgress = ref(0)
const contentReady = ref(false)

// Scripts
const lendioPerformanceLogging = {
  innerHTML: `
    window.LendioPerformanceLogging = {
        debug: false,
        baseRouteChangeStart: {
          logged: false,
          startTime: null
        },
        baseTime: {
          startTime: new Date(),
          logged: false
        },
        calculateLoadTime: function(basetime, time) {
          time = time || new Date();
          basetime = basetime || this.baseTime.startTime;
          return time - basetime;
        }
      }
  `,
  type: 'text/javascript'
}

const dreams = { src: env('dreamsUrl', '') }

//TODO remove when Finicity Connect experiment is complete
const finicityConnectScript = {
  src: "https://connect2.finicity.com/assets/sdk/finicity-connect.min.js",
  tagPosition: 'bodyClose',
  defer: true,
}

const docusignScript = {
  src: process.env.NODE_ENV === 'LIVE' || process.env.NODE_ENV === 'production' ?
    'https://js.docusign.com/bundle.js' :
    'https://js-d.docusign.com/bundle.js',
  tagPosition: 'bodyClose',
  defer: true,
}

const gtm = {
  innerHTML: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
    'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
  })(window,document,'script','dataLayer','GTM-P6PJHT')`,
  type: 'text/javascript'
}
const arrayAtPolyfill = {
  src: `${nuxtBaseUrl}/array-at.polyfill.min.js`,
}
useHead({
  htmlAttrs: {
    lang: 'en'
  },
  base: {
    href: `${nuxtBaseUrl}/`
  },
  meta: [
    { 'http-equiv': 'X-UA-Compatible', content: 'IE=edge,chrome=1' },
    { 'http-equiv': 'content-type', content: 'text/html; charset=UTF-8' },
    { 'name': 'URL', content: 'http://www.lendio.com' },
    { 'name': 'robots', content: 'noindex' },
    { 'name': 'language', content: 'ENG' },
    { 'name': 'subject', content: '' },
    { 'name': 'viewport', content: 'width=device-width, initial-scale=1' },
    { 'name': 'msapplication-config', content: '/browserconfig.xml' },
    { 'name': 'theme-color', content: DEFAULT_THEME_COLOR },
  ],
  link: [
    { rel: 'apple-touch-icon', sizes: '180x180', href: '/images/apple-touch-icon.png'},
    { rel: 'manifest', sizes: '180x180', href: '/images/manifest.json'},
    // preconnect for resources that load immediately (GTM inline script)
    { rel: 'preconnect', href: 'https://www.googletagmanager.com' },
    { rel: 'preconnect', href: 'https://www.google-analytics.com' },
    { rel: 'preconnect', href: 'https://tools.lendio.com' },
    { rel: 'preconnect', href: 'https://use.typekit.net', crossorigin: '' },
    { rel: 'preload', href: 'https://use.typekit.net/kih7hlm.css', as: 'style' },

    // dns-prefetch for deferred/late-loading scripts
    { rel: 'dns-prefetch', href: 'https://p.typekit.net' },
    { rel: 'dns-prefetch', href: 'https://js.docusign.com' },
    { rel: 'dns-prefetch', href: 'https://js-d.docusign.com' },
    { rel: 'dns-prefetch', href: 'https://connect2.finicity.com' },
  ],
  titleTemplate: (string) => string
})

useHead({
  script: [
    lendioPerformanceLogging,
    gtm,
    finicityConnectScript,
    docusignScript,
    dreams,
    arrayAtPolyfill,
  ].filter(x => !!x)
})

</script>
<style lang="scss">
  @import '/assets/styles/mp-transitions.scss';
</style>
